import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export type RowAction = {
  label: string;
  onSelect: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  destructive?: boolean;
  /** Present and non-empty means the item is disabled, and this says why. */
  disabledReason?: string;
};

/**
 * A row's actions, behind one button.
 *
 * Administration tables had three to five buttons per row, which made every row a toolbar and
 * every column narrower than its content. One trigger, a menu, and the row is legible again.
 *
 * **A refusal explains itself here rather than in the row.** The stages table used to print
 * "holds 4 opportunities" in a column of its own — a permanent sentence about a rule that only
 * matters at the moment you try to break it. A blocked action is disabled and carries the
 * reason as its own hint, shown where the reader's attention already is. Radix keeps a disabled
 * item focusable, so unlike a disabled *button* the reason is reachable by keyboard.
 */
export function RowActions({ label, actions }: { label: string; actions: RowAction[] }): React.JSX.Element | null {
  if (actions.length === 0) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={label}
        className="hover:bg-accent focus-visible:ring-ring rounded-md p-1.5 focus-visible:ring-2 focus-visible:outline-none"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          // z-50, as the app's z-index ladder requires of every Radix overlay.
          className="bg-popover text-popover-foreground z-50 min-w-48 rounded-md border p-1 shadow-md"
        >
          {actions.map((action) => {
            const Icon = action.icon;
            const blocked = Boolean(action.disabledReason);

            return (
              <DropdownMenu.Item
                key={action.label}
                disabled={blocked}
                // `select` on a disabled item is a no-op in Radix; the guard is here as well so
                // a future refactor of this component cannot turn a hint into an action.
                onSelect={() => {
                  if (!blocked) action.onSelect();
                }}
                className={cn(
                  "data-[highlighted]:bg-accent flex cursor-pointer flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-sm outline-none",
                  action.destructive && "text-destructive",
                  blocked && "cursor-not-allowed opacity-60",
                )}
              >
                <span className="flex items-center gap-2">
                  {Icon && <Icon className="h-4 w-4" aria-hidden />}
                  {action.label}
                </span>
                {action.disabledReason && (
                  // The reason travels with the item, so it is read on hover *and* on focus —
                  // the two ways a reader arrives at a control they cannot use.
                  <span className="text-muted-foreground pl-6 text-xs">{action.disabledReason}</span>
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
